import React from 'react';
import { Alert, Box, Typography, Button, Divider, Stack, Chip } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import UndoIcon from '@mui/icons-material/Undo';
import { useSelector, useDispatch } from 'react-redux';

import {
  selectAppliedPromotions,
  selectCartItems,
  selectCartTotals,
  selectQuotedTotals,
  selectQuote,
  clearCart,
} from '../store/cartSlice';
import CartItem from './CartItem';

/**
 * Cart panel showing line items and totals.
 * Reads from the Redux store.
 */
export default function Cart({ onCheckout, onReturns, quoteLoading, quoteError }) {
  const dispatch = useDispatch();
  const items = useSelector(selectCartItems);
  const { subtotal, itemCount } = useSelector(selectCartTotals);
  const quote = useSelector(selectQuote);
  const quotedTotals = useSelector(selectQuotedTotals);
  const appliedPromotions = useSelector(selectAppliedPromotions);
  const quotedItemsByCode = (quote?.items || []).reduce((acc, item) => {
    acc[item.item_code] = item;
    return acc;
  }, {});

  if (items.length === 0) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          gap: 1,
        }}
      >
        <ShoppingCartIcon sx={{ fontSize: 48, opacity: 0.3 }} />
        <Typography variant="body2">Cart is empty</Typography>
        <Typography variant="caption">Click items to add them</Typography>
        {onReturns && (
          <Button
            size="small"
            startIcon={<UndoIcon />}
            onClick={onReturns}
            sx={{ mt: 2 }}
          >
            Process Return
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {itemCount} item{itemCount !== 1 ? 's' : ''}
        </Typography>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={() => dispatch(clearCart())}
        >
          Clear
        </Button>
      </Stack>

      <Divider />

      {/* Scrollable item list */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {items.map((item) => (
          <CartItem
            key={item.item_code}
            item={item}
            quotedItem={quotedItemsByCode[item.item_code]}
          />
        ))}
      </Box>

      {/* Totals + Checkout */}
      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        {quoteError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Could not refresh promotion pricing. Local totals are shown.
          </Alert>
        )}

        {appliedPromotions.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
            {appliedPromotions.map((rule) => (
              <Chip key={rule} label={rule} size="small" color="success" variant="outlined" />
            ))}
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Subtotal {quoteLoading ? '(updating...)' : ''}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {subtotal.toFixed(2)}
          </Typography>
        </Stack>

        {quotedTotals.discountAmount > 0 && (
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="success.main">Promotion Discount</Typography>
            <Typography variant="body2" color="success.main" fontWeight={600}>
              -{quotedTotals.discountAmount.toFixed(2)}
            </Typography>
          </Stack>
        )}

        {quotedTotals.taxes.length > 0 && quotedTotals.taxes.map((tax) => (
          <Stack key={tax.description} direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{tax.description}</Typography>
            <Typography variant="body2">{(tax.tax_amount || 0).toFixed(2)}</Typography>
          </Stack>
        ))}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="h6">Total</Typography>
          <Typography variant="h6" fontWeight={700}>
            {quotedTotals.roundedTotal.toFixed(2)}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          {onReturns && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<UndoIcon />}
              onClick={onReturns}
              sx={{ flexShrink: 0 }}
            >
              Return
            </Button>
          )}
          {onCheckout && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={onCheckout}
            >
              Checkout (F2)
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
